/**
 * PDF type mapping utilities
 * Centralized functions for converting between PDF display names and internal types
 */

/**
 * Maps PDF display names to internal PDF types
 * @param name - The display name (e.g., "Summary", "Worksheet 1")
 * @returns The internal PDF type (e.g., "summary", "worksheet1") or undefined if not found
 */
export function getPdfMapByName(name: string): string | undefined {
    const pdfTypeMap: Record<string, string> = {
        'Summary': 'summary',
        'Worksheet 1': 'worksheet1',
        'Financial Affidavit': 'financial',
        'Division Worksheet': 'division',
        'Summary + Worksheet 1': 'summaryandworksheet1',
        'High Income Estimate': 'highIncomeEstimate',
        'Alimony Analysis': 'alimonyAnalysis'
    };
    return pdfTypeMap[name];
}

/**
 * Maps internal PDF types to display names
 * @param type - The internal PDF type (e.g., "summary", "worksheet1")
 * @returns The display name (e.g., "Summary", "Worksheet 1") or the original type if not found
 */
export function getPdfMapByType(type: string): string {
    const pdfTypeMap: Record<string, string> = {
        'summary': 'Summary',
        'worksheet1': 'Worksheet 1',
        'financial': 'Financial Affidavit',
        'division': 'Division Worksheet',
        'summaryandworksheet1': 'Summary + Worksheet 1',  // database stores all lowercase
        'highincomeestimate': 'High Income Estimate',
        'alimonyanalysis': 'Alimony Analysis',
        'calculonallocationadvice': 'Allocation Analysis',
        'calculon_allocation_advice': 'Allocation Analysis', // legacy rows before camelCase pdf_type
    };
    return pdfTypeMap[type.toLowerCase()] || type;
}

/**
 * Maps internal PDF types to server enum values
 * @param type - The internal PDF type (e.g., "summary", "worksheet1", "summary_and_worksheet_1")
 * @returns The server enum value (e.g., "Summary", "Worksheet1") or the original type if not found
 */
export function getPdfTypeToServerEnum(type: string): string {
    const pdfTypeMap: Record<string, string> = {
        'summary': 'Summary',
        'worksheet1': 'Worksheet1',
        'financial': 'Financial',
        'division': 'Division',
        'summaryandworksheet1': 'Summary + Worksheet 1',
        'highincomeestimate': 'HighIncomeEstimate',
        'alimonyanalysis': 'AlimonyAnalysis',
        'calculonallocationadvice': 'Allocation Analysis',
        'calculon_allocation_advice': 'Allocation Analysis', // legacy rows before camelCase pdf_type
    };
    return pdfTypeMap[type.toLowerCase()] || type;
}

